/* eslint-disable */
import React, { useEffect, useMemo, useState } from "react";
// ANT DESIGN COMPONENTS
import { Tooltip } from "antd";
// REDUX
import { useSelector } from "react-redux";
// I18N TRANSLATION
import { useTranslation } from "react-i18next";
// CUSTOM HOOKS
import { useLastBankMessage } from "../socketMessageState/BankMessagesContext";
// COMPONENTS
import IconDefinitionElevatorCustom from "../iconDefinition/IconDefinitionElevatorCustom";
import IconDefinitionElevatorDefault from "../iconDefinition/IconDefinitionElevatorDefault";
import MonitoringElevatorFullViewName from "./MonitoringElevatorFullViewName";
import IconDefinition from "../iconDefinition/IconDefinition";
// INTERFACES
import { LiftInterface } from "../../../interfaces/Lift.interface";
import { BankInterface, BankFloorInterface } from "../../../interfaces/Bank.interface";
import { RootState } from "../../../app/store";
import { SocketMessageInterface } from "../../../interfaces/Monitoring.interface";
// CONSTANTS
import { BLANK_SOCKET_MESSAGE } from "../../../constants/monitoring";
// UTILS
import { stringToUuidT } from "../../../utils/uuid";

function MonitoringElevatorsFullView({
  bank,
  serverId,
  channelId,
  enableActions,
}: {
  bank: BankInterface;
  serverId: string;
  channelId: string;
  enableActions: boolean;
}) {
  // ************************************************ */
  // GLOBAL VARIABLES ******************************* */
  const [t] = useTranslation("global");
  const customConfiguration = bank?.adapter?.socket_message_priority?.customConfiguration;

  // ************************************************ */
  // USE STATE VARIABLES **************************** */
  const [bankId, setBankId] = useState<string>("");

  // ************************************************ */
  // REDUX SLICE VARIABLES ************************** */
  const theme = useSelector((state: RootState) => state.home.theme);
  const openReplayPanel = useSelector((state: RootState) => state.monitoring.openReplayPanel);

  // ************************************************ */
  // CUSTOM HOOKS *********************************** */
  const bankUuid = useMemo(() => stringToUuidT(bankId), [bankId]);
  const socketMessageBank: SocketMessageInterface =
    useLastBankMessage(bankUuid) || BLANK_SOCKET_MESSAGE;

  // ************************************************ */
  // SERVICES AND API CALLS ************************* */

  // ************************************************ */
  // FUNCTIONS ************************************** */

  // ************************************************ */
  // USE EFFECT ************************************* */
  const activeHallCalls = useMemo(() => {
    if (!socketMessageBank) {
      return { hallCalls: new Set<number>(), upCalls: new Set<number>() };
    }

    const hallCalls = new Set<number>();
    const upCalls = new Set<number>();

    [
      ...socketMessageBank.hallCalls.up.front,
      ...socketMessageBank.hallCalls.up.rear,
      ...socketMessageBank.hallCalls.down.front,
      ...socketMessageBank.hallCalls.down.rear,
    ].forEach((f) => hallCalls.add(f));

    [...socketMessageBank.hallCalls.up.front, ...socketMessageBank.hallCalls.up.rear].forEach((f) =>
      upCalls.add(f),
    );

    return { hallCalls, upCalls };
  }, [socketMessageBank]);

  const doorConfigByLiftId = useMemo(() => {
    const configMap = new Map<string, { frontDoors: boolean; rearDoors: boolean }>();

    (bank.lifts || []).forEach((lift: LiftInterface) => {
      const frontDoors = lift.lift_floors?.some((liftFloor) => liftFloor.front_door) ?? false;
      const rearDoors = lift.lift_floors?.some((liftFloor) => liftFloor.rear_door) ?? false;

      configMap.set(lift.id, { frontDoors, rearDoors });
    });

    return configMap;
  }, [bank.lifts]);

  useEffect(() => {
    if (bank && bank.id && bank.id !== "") {
      setBankId(bank.id);
    }
  }, [bank]);

  // ************************************************ */
  // COMPONENT ************************************** */
  return (
    <div className="monitoring__elevatorFullView__container">
      {bank.bank_floors &&
        bank.bank_floors
          .slice()
          .sort((a: BankFloorInterface, b: BankFloorInterface) => b.floor_index - a.floor_index)
          .map((floor: BankFloorInterface, floorIndex: number) => {
            const hasHallCall = activeHallCalls.hallCalls.has(floor.floor_index);
            const hasUpCall = activeHallCalls.upCalls.has(floor.floor_index);
            return (
              <div key={floor.id}>
                <div className="generalStyles__flex" key={floor.id}>
                  {/** *********************************************************** */}
                  {/** LEFT FLOORS / FRONT DOOR FLOORS */}
                  <div
                    className={`monitoring__card__floor generalStyles__mlFix ${
                      hasHallCall
                        ? hasUpCall
                          ? `monitoring__card__floor__active__green__${theme}`
                          : `monitoring__card__floor__active__red__${theme}`
                        : floor.front_door
                        ? `monitoring__card__floor__door__${theme}`
                        : `monitoring__card__floor__inactive__${theme}`
                    }`}
                  >
                    {socketMessageBank.hallCalls.up.frontLock.includes(floor.floor_index) ||
                    socketMessageBank.hallCalls.down.frontLock.includes(floor.floor_index) ? (
                      <Tooltip title={t("general.frontDoorLocked")}>
                        <div>
                          <IconDefinition
                            isMap={false}
                            expanded={false}
                            liftType="ELEVATOR"
                            icon="elLocked"
                            iconSize={13}
                          />
                        </div>
                      </Tooltip>
                    ) : (
                      <Tooltip title={floor.name}>
                        {openReplayPanel ? (
                          <span className="generalStyles__warning">{floor.floor_index}</span>
                        ) : (
                          <span>{floor?.name?.toUpperCase().substring(0, 2) || "--"}</span>
                        )}
                      </Tooltip>
                    )}
                  </div>
                  {/** *********************************************************** */}
                  {/** DOTS AND ELEVATOR CONTAINER */}
                  <div className="monitoring__elevatorFullView__dotsContainer">
                    {/** LIFTS */}
                    <div
                      className="monitoring__elevatorFullView__liftContainer"
                      style={{
                        display: "flex",
                        gap: "6px",
                        justifyContent: "center",
                      }}
                    >
                      {bank.lifts
                        .slice()
                        .sort((a: LiftInterface, b: LiftInterface) => a.position - b.position)
                        .map((lift: LiftInterface) => {
                          const liftDoorConfig = doorConfigByLiftId.get(lift.id) || {
                            frontDoors: false,
                            rearDoors: false,
                          };

                          return (
                            <div
                              key={lift.id}
                              style={{
                                flex: "1 1 0",
                                minWidth: 0,
                              }}
                            >
                              {customConfiguration ? (
                                <IconDefinitionElevatorCustom
                                  floor={floor}
                                  lift={lift}
                                  bank={bank}
                                  floorIndex={floorIndex}
                                  adapterRules={bank?.adapter?.socket_message_priority?.rules ?? []}
                                />
                              ) : (
                                <IconDefinitionElevatorDefault
                                  floor={floor}
                                  lift={lift}
                                  bank={bank}
                                  floorIndex={floorIndex}
                                  frontDoors={liftDoorConfig.frontDoors}
                                  rearDoors={liftDoorConfig.rearDoors}
                                />
                              )}
                            </div>
                          );
                        })}
                    </div>
                    {/** DOTS STYLE */}
                    <div className="monitoring__elevatorFullView__dotsStyleContainer">
                      <div className={`monitoring__card__dotStyle__${theme}__inactive`} />
                    </div>
                    {/** UP AND DOWN HALL CALLS DOTS STYLE */}
                    <div className="monitoring__elevatorFullView__dotsStyleContainer">
                      <div
                        className={`${
                          hasHallCall
                            ? hasUpCall
                              ? "monitoring__card__dotStyle__hallCalls__green"
                              : "monitoring__card__dotStyle__hallCalls__red"
                            : ""
                        }`}
                      />
                    </div>
                  </div>
                  {/** *********************************************************** */}
                  {/** RIGHT FLOORS / FRONT DOOR FLOORS */}
                  <div
                    className={`monitoring__card__floor ${
                      hasHallCall
                        ? hasUpCall
                          ? `monitoring__card__floor__active__green__${theme}`
                          : `monitoring__card__floor__active__red__${theme}`
                        : floor.rear_door
                        ? `monitoring__card__floor__door__${theme}`
                        : `monitoring__card__floor__inactive__${theme}`
                    }`}
                  >
                    {socketMessageBank.hallCalls.up.rearLock.includes(floor.floor_index) ||
                    socketMessageBank.hallCalls.down.rearLock.includes(floor.floor_index) ? (
                      <Tooltip title={t("general.rearDoorLocked")}>
                        <div>
                          <IconDefinition
                            isMap={false}
                            expanded={false}
                            liftType="ELEVATOR"
                            icon="elLocked"
                            iconSize={13}
                          />
                        </div>
                      </Tooltip>
                    ) : (
                      <Tooltip title={floor.name}>
                        {openReplayPanel ? (
                          <span className="generalStyles__warning">{floor.floor_index}</span>
                        ) : (
                          <span>{floor?.name?.toUpperCase().substring(0, 2) || "--"}</span>
                        )}{" "}
                      </Tooltip>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
      {/** *********************************************************** */}
      {/** LIFT NAMES ROW (LAST FLOOR) */}
      <div className="generalStyles__flex">
        <div className="monitoring__card__floor generalStyles__mlFix">
          <span />
        </div>
        <div className="monitoring__elevatorFullView__dotsContainer">
          <div
            className="monitoring__elevatorFullView__liftContainer"
            style={{
              display: "flex",
              gap: "6px",
              justifyContent: "center",
            }}
          >
            {bank.lifts
              .slice()
              .sort((a: LiftInterface, b: LiftInterface) => a.position - b.position)
              .map((lift: LiftInterface) => (
                <div
                  style={{
                    flex: "1 1 0",
                    minWidth: 0,
                    textAlign: "center",
                  }}
                  key={lift.id}
                >
                  <MonitoringElevatorFullViewName
                    lift={lift}
                    bank={bank}
                    serverId={serverId}
                    channelId={channelId}
                    enableActions={enableActions}
                  />
                </div>
              ))}
          </div>
        </div>
        <div className="monitoring__card__floor">
          <span />
        </div>
      </div>
    </div>
  );
}

export default MonitoringElevatorsFullView;
